import * as React from 'react';
import { DefaultButton, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import { useParams, useSearchParams } from 'react-router-dom';
import { loadComputeFilterOptions } from './computeApi';
import { defaultComputeFilters, readComputeFilters, writeComputeFilters } from './computeFilters';
import {
    ComputeChart,
    ComputeTableView,
    exportResult,
    FilterBar,
    type FilterBarDimension,
    type FilterBarField,
    Freshness,
    SummaryCards,
} from './ComputeInsightsPage';
import type { ComputeFilterOptions, ComputeFilters } from './computeTypes';
import type { ComputeDashboardCatalogItem, ComputeDashboardView, DashboardFilterOptions, DashboardSelections } from './dashboardTypes';
import { loadComputeDashboard, loadComputeDashboards, loadDashboardFilterOptions, hasDashboardVisuals } from './dashboardApi';
import { ConfigDashboardVisuals } from './ConfigDashboardVisuals';
import '../../styles/ComputeInsights.css';
import '../../styles/ConfigDashboard.css';

// Renders any dashboard defined by a JSON file on the server; nothing here is specific to one report.
export function ConfigDashboardPage() {
    const { dashboardId = '' } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [metadata, setMetadata] = React.useState<ComputeDashboardCatalogItem | null>(null);
    const dashboard = metadata?.id === dashboardId ? metadata : null;
    const [metadataError, setMetadataError] = React.useState<string | null>(null);
    const filters = React.useMemo(() => readComputeFilters(searchParams), [searchParams]);
    const [result, setResult] = React.useState<ComputeDashboardView | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [filterOptions, setFilterOptions] = React.useState<ComputeFilterOptions | null>(null);
    const [dimensionOptions, setDimensionOptions] = React.useState<DashboardFilterOptions | null>(null);
    const [filterError, setFilterError] = React.useState<string | null>(null);
    const [dimensionErrors, setDimensionErrors] = React.useState<Record<string, string>>({});
    const [optionsAttempt, setOptionsAttempt] = React.useState(0);
    const selections = React.useMemo<DashboardSelections>(() => Object.fromEntries(
        (dashboard?.dimensionFilters ?? []).map(filter => [filter.id,
            filter.parameterFormat === 'json' && searchParams.has(`${filter.id}[]`)
                ? searchParams.getAll(`${filter.id}[]`) : searchParams.get(filter.id)?.trim() ?? '']),
    ), [searchParams, dashboard]);
    const invalidRange = filters.startDate > filters.endDate;

    React.useEffect(() => {
        document.title = `SPO Compute - ${result?.title ?? 'Dashboard'}`;
    }, [result?.title]);

    React.useEffect(() => {
        const controller = new AbortController();
        setMetadata(null);
        setMetadataError(null);
        setResult(null);
        loadComputeDashboards(controller.signal).then(items => {
            if (controller.signal.aborted) return;
            const item = items.find(item => item.id === dashboardId);
            if (!item) throw new Error(`Dashboard '${dashboardId}' was not found.`);
            setMetadata(item);
        }).catch((error: unknown) => {
            if (!controller.signal.aborted) {
                setMetadataError(error instanceof Error ? error.message : 'Unable to load dashboard configuration.');
            }
        });
        return () => controller.abort();
    }, [dashboardId]);

    React.useEffect(() => {
        const controller = new AbortController();
        setFilterOptions(null);
        setDimensionOptions(null);
        setDimensionErrors({});
        setFilterError(null);
        if (!dashboard) return () => controller.abort();
        const requests: Promise<unknown>[] = [];
        if (dashboard.filters.some(filter => filter === 'geo' || filter === 'farm' || filter === 'customerSegment')) {
            requests.push(loadComputeFilterOptions(controller.signal).then(options => {
                if (!controller.signal.aborted) setFilterOptions(options);
            }));
        }
        for (const filter of dashboard.dimensionFilters ?? []) {
            void loadDashboardFilterOptions(dashboard.id, controller.signal, filter.id).then(options => {
                if (!Array.isArray(options[filter.id])) throw new Error('Dashboard filter options response is incomplete.');
                if (!controller.signal.aborted) setDimensionOptions(current => ({ ...current, [filter.id]: options[filter.id] }));
            }).catch((error: unknown) => {
                if (!controller.signal.aborted) setDimensionErrors(current => ({
                    ...current, [filter.id]: error instanceof Error ? error.message : 'Unable to load options.',
                }));
            });
        }
        Promise.all(requests).catch((error: unknown) => {
            if (!controller.signal.aborted) {
                setFilterError(error instanceof Error ? error.message : 'Unable to load filter options.');
            }
        });
        return () => controller.abort();
    }, [dashboard, optionsAttempt]);

    React.useEffect(() => {
        if (!dashboard || invalidRange) {
            setResult(null);
            setError(null);
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        setLoading(true);
        setError(null);
        loadComputeDashboard(dashboardId, filters, controller.signal, selections)
            .then(data => {
                if (controller.signal.aborted) return;
                setResult(data);
                setLoading(false);
            })
            .catch((requestError: unknown) => {
                if (controller.signal.aborted) {
                    return;
                }
                setResult(null);
                setError(requestError instanceof Error ? requestError.message : 'Unable to load this dashboard.');
                setLoading(false);
            });
        return () => controller.abort();
    }, [dashboardId, dashboard, filters, invalidRange, selections]);

    const fields = React.useMemo<FilterBarField[]>(
        () => [
            ...(dashboard?.filters ?? []).filter(
                (filter): filter is 'geo' | 'farm' | 'customerSegment' => filter === 'geo' || filter === 'farm' || filter === 'customerSegment',
            ),
            ...(result && dashboard && !hasDashboardVisuals(result) && result.charts.every(chart => chart.xAxis === 'time')
                ? ['granularity' as const] : []),
        ],
        [dashboard, result],
    );
    const showsGranularity = fields.includes('granularity');

    // Keep granularity out of the URL while Group by is hidden, so per-category dashboards do not carry granularity=day.
    const writeFilters = React.useCallback((current: URLSearchParams, next: ComputeFilters) => {
        const params = writeComputeFilters(current, next);
        if (!showsGranularity) params.delete('granularity');
        return params;
    }, [showsGranularity]);

    const updateFilters = React.useCallback((updates: Partial<ComputeFilters>) => {
        setSearchParams(writeFilters(searchParams, { ...filters, ...updates }), { replace: true });
    }, [filters, searchParams, setSearchParams, writeFilters]);

    const resetFilters = React.useCallback(() => {
        setSearchParams(writeFilters(new URLSearchParams(), defaultComputeFilters()), { replace: true });
    }, [setSearchParams, writeFilters]);

    // No values removes the parameter, which means all values, like Geo. JSON filters repeat id[] so values may hold commas.
    const updateSelection = React.useCallback((id: string, values: string[]) => {
        const next = new URLSearchParams(searchParams);
        next.delete(id);
        next.delete(`${id}[]`);
        const exact = dashboard?.dimensionFilters?.find(filter => filter.id === id)?.parameterFormat === 'json';
        if (values.length) {
            const ordered = [...new Set(values)].sort();
            if (exact) for (const value of ordered) next.append(`${id}[]`, value);
            else next.set(id, ordered.join(','));
        }
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, dashboard]);

    const dimensions = React.useMemo<FilterBarDimension[]>(
        () => (dashboard?.dimensionFilters ?? []).map(filter => {
            const value = selections[filter.id] ?? '';
            return {
                id: filter.id,
                label: filter.label,
                value: Array.isArray(value) ? value : value.split(',').map(item => item.trim()).filter(Boolean),
                options: dimensionOptions?.[filter.id] ?? null,
                unavailable: Boolean(dimensionErrors[filter.id]),
                onChange: (values: string[]) => updateSelection(filter.id, values),
            };
        }),
        [dashboard, selections, dimensionOptions, dimensionErrors, updateSelection],
    );

    return (
        <main className="compute-page config-dashboard-page page-container">
            <header className="compute-heading">
                <div>
                    <div className="compute-eyebrow">SPO Compute Insights</div>
                    <h1>{dashboard?.title ?? (metadataError ? 'Dashboard' : 'Loading...')}</h1>
                    {dashboard?.description && <p>{dashboard.description}</p>}
                </div>
                <div className="compute-heading-status">
                    <Freshness freshness={result?.freshness} loading={loading} />
                </div>
            </header>

            <FilterBar
                filters={filters}
                options={filterOptions}
                onChange={updateFilters}
                onReset={resetFilters}
                fields={fields}
                dimensions={dimensions}
            />

            {invalidRange && (
                <MessageBar messageBarType={MessageBarType.warning}>Start date must be on or before end date.</MessageBar>
            )}
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {metadataError && <MessageBar messageBarType={MessageBarType.error}>{metadataError}</MessageBar>}
            {filterError && <MessageBar messageBarType={MessageBarType.error}>{filterError}</MessageBar>}
            {dashboard?.dimensionFilters?.filter(filter => dimensionErrors[filter.id]).map(filter => (
                <MessageBar key={filter.id} messageBarType={MessageBarType.error}
                    actions={<DefaultButton text="Retry" ariaLabel={`Retry ${filter.label} options`}
                        onClick={() => setOptionsAttempt(value => value + 1)} />}>
                    {dimensionErrors[filter.id]}
                </MessageBar>
            ))}

            {loading || (!dashboard && !metadataError) ? (
                <div className="compute-loading"><Spinner size={SpinnerSize.large} label="Loading dashboard..." /></div>
            ) : result && !invalidRange ? (
                <>
                    {result.summary.length > 0 && <SummaryCards metrics={result.summary} />}
                    {hasDashboardVisuals(result) ? (
                        <ConfigDashboardVisuals
                            visuals={result.visuals!}
                            onSelectCategory={(filterId, category) => updateSelection(filterId, [category])}
                        />
                    ) : result.charts.map((chart, index) => (
                        <section className="compute-panel" key={index}>
                            <div className="compute-panel-heading">
                                <div>
                                    <h2>{chart.title || 'Trend'}</h2>
                                    <span>{filters.startDate} to {filters.endDate}</span>
                                </div>
                            </div>
                            <ComputeChart series={chart.series} bars={chart.bars} type={chart.type} xAxis={chart.xAxis} />
                        </section>
                    ))}
                    <section className="compute-panel">
                        <div className="compute-panel-heading">
                            <div>
                                <h2>Details</h2>
                                <span>{result.table.rows.length.toLocaleString()} rows</span>
                            </div>
                            <DefaultButton
                                text="Export CSV"
                                iconProps={{ iconName: 'Download' }}
                                disabled={result.table.rows.length === 0}
                                onClick={() => exportResult(dashboardId, result)}
                            />
                        </div>
                        <ComputeTableView result={result} />
                    </section>
                </>
            ) : null}
        </main>
    );
}

